import React from "react";
import { CustomHeader } from "react-calendar-timeline";

/*
 * The primary (top) date row.
 *
 * The timeline makes it one unit coarser than the grid, so zoomed in to
 * minutes its cells were hours, and every hour spelled out the same date.
 * Those hours are merged into one cell per day; the label stays in view while
 * the day scrolls past (the sticky .rct-dateHeader-primary span in style.css).
 *
 * The row is built from the grid's own cells: a CustomHeader given no unit
 * uses the timeline's. It must not be given "primaryHeader" — only DateHeader
 * understands that; CustomHeader steps through time by it, which moment
 * cannot add, and loops forever.
 */
const NEXT_UNIT = {
  minute: "hour",
  hour: "day",
  day: "month",
  week: "month",
  month: "year",
  year: "year",
};

export const nextUnit = (unit) => NEXT_UNIT[unit] || "year";

// The row's cells from the grid's intervals, in the grid's unit: the interval
// drawn, the unit its label is written in, and the period a click on it shows
// (as the timeline's own header does: the next unit up from the row's).
export const primaryCells = (intervals, gridUnit) => {
  const rowUnit = nextUnit(gridUnit);
  const cellUnit = rowUnit === "hour" ? "day" : rowUnit;
  const zoomUnit = nextUnit(rowUnit);
  const cells = [];
  intervals.forEach((interval) => {
    const cell = cells[cells.length - 1];
    if (cell && cell.interval.startTime.isSame(interval.startTime, cellUnit)) {
      cell.interval = {
        ...cell.interval,
        endTime: interval.endTime,
        labelWidth: cell.interval.labelWidth + interval.labelWidth,
      };
    } else {
      cells.push({ interval: { ...interval }, labelUnit: cellUnit, zoomUnit });
    }
  });
  return cells;
};

// `label([startTime, endTime], unit)` writes a cell's date.
export const PrimaryDateHeader = ({ label }) => (
  <CustomHeader>
    {({
      headerContext: { intervals, unit },
      getRootProps,
      getIntervalProps,
      showPeriod,
    }) => (
      <div
        {...getRootProps({
          style: { backgroundColor: "var(--color-surface)" },
        })}
      >
        {primaryCells(intervals, unit).map(
          ({ interval, labelUnit, zoomUnit }) => (
            <div
              {...getIntervalProps({ interval })}
              key={interval.startTime.valueOf()}
              className="rct-dateHeader rct-dateHeader-primary"
              onClick={() =>
                showPeriod(
                  interval.startTime.clone().startOf(zoomUnit),
                  interval.startTime.clone().endOf(zoomUnit),
                )
              }
            >
              <span>
                {label([interval.startTime, interval.endTime], labelUnit)}
              </span>
            </div>
          ),
        )}
      </div>
    )}
  </CustomHeader>
);
